import React, { createContext, useContext, useRef, useState } from "react"

import { PopOver, PopOverContent } from "#React/ui/PopOver"
import cn from "#Utils/cn"
import { isSidebarCollapsed } from "#Utils/sidebarCollapse"

import Link, { type LinkProps } from "../shared/link"
import { useSidebarSubmenuState } from "./useSidebarSubmenuState"

type SidebarPopoverItem = Pick<LinkProps, "href" | "to" | "params" | "search"> & {
  active?: boolean
  hint?: string
  items?: SidebarPopoverItem[]
  title: string
  type?: "group"
  scope?: string[]
}

type SidebarPopoverContextValue = {
  isCollapsed: () => boolean
  isOpenFor: (anchor: HTMLElement | null) => boolean
  popoverId: string
  scheduleClose: () => void
  show: (popover: {
    anchor: HTMLElement
    items?: SidebarPopoverItem[]
    maxHeight: number
    title: string
    focusFirst?: boolean
  }) => void
}

type SidebarPopoverState = {
  anchor: HTMLElement | null
  items: SidebarPopoverItem[]
  maxHeight: number
  open: boolean
  title: string
}

const SidebarPopoverContext = createContext<SidebarPopoverContextValue | undefined>(undefined)

export const useSidebarPopover = () => {
  return useContext(SidebarPopoverContext)
}

const SidebarPopoverGroup = ({ item }: { item: SidebarPopoverItem }) => {
  const [isOpen, setIsOpen] = useSidebarSubmenuState(item.title, item.scope)

  return (
    <li>
      <button
        type="button"
        className="flex items-center gap-1 w-full py-0.5 text-left text-ms c-heading--caps text-content-secondary focus:outline-hidden cursor-pointer mb-0!"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="grow">{item.title}</span>
        <i
          className={cn(
            "far fa-fw c-side-nav__subitem-icon",
            isOpen ? "fa-angle-up" : "fa-angle-down",
          )}
        />
      </button>
      {isOpen && item.items && (
        <div className="ml-1 border-l border-edge-default pl-2">
          <SidebarPopoverItems items={item.items} />
        </div>
      )}
    </li>
  )
}

const SidebarPopoverItems = ({ items }: { items: SidebarPopoverItem[] }) => {
  return (
    <ul className="mb-1">
      {items.map((item, index) => {
        const itemKey = `${item.title}-${index}`

        if (item.type === "group") {
          return <SidebarPopoverGroup key={itemKey} item={item} />
        }

        return (
          <li key={itemKey}>
            <Link
              href={item.href}
              to={item.to}
              params={item.params}
              search={item.search}
              title={item.hint}
              aria-current={item.active ? "page" : undefined}
              className={cn(
                " py-0.5 rounded-sm px-1 block text-sm no-underline hover:underline focus:outline-hidden hover:bg-surface-highlight",
                {
                  "font-semibold text-link": item.active,
                },
              )}
            >
              {item.title}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

export const SidebarPopoverProvider = ({ children, collapsed }: { children: React.ReactNode; collapsed?: boolean }) => {
  const collapsedNow = () => collapsed ?? isSidebarCollapsed()
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const focusFirstLinkRef = useRef(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const popoverId = React.useId()
  const [popover, setPopover] = useState<SidebarPopoverState>({
    anchor: null,
    items: [],
    maxHeight: 0,
    open: false,
    title: "",
  })

  React.useEffect(() => () => clearTimeout(closeTimeoutRef.current), [])

  React.useEffect(() => {
    if (popover.open && focusFirstLinkRef.current) {
      contentRef.current?.querySelector<HTMLAnchorElement>("a[href]")?.focus()
      focusFirstLinkRef.current = false
    }
  }, [popover])

  const keepOpen = () => {
    clearTimeout(closeTimeoutRef.current)
  }

  const scheduleClose = () => {
    clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      setPopover((currentPopover) => ({ ...currentPopover, open: false }))
    }, 100)
  }

  const show = ({
    anchor,
    items = [],
    maxHeight,
    title,
    focusFirst = false,
  }: Parameters<SidebarPopoverContextValue["show"]>[0]) => {
    if (!collapsedNow()) return

    clearTimeout(closeTimeoutRef.current)
    if (focusFirst) {
      if (popover.open && popover.anchor === anchor) {
        contentRef.current?.querySelector<HTMLAnchorElement>("a[href]")?.focus()
      } else {
        focusFirstLinkRef.current = true
      }
    }
    setPopover({ anchor, items, maxHeight, open: true, title })
  }

  return (
    <SidebarPopoverContext.Provider
      value={{
        isCollapsed: collapsedNow,
        isOpenFor: (anchor) => popover.open && collapsedNow() && popover.anchor === anchor,
        popoverId,
        scheduleClose,
        show,
      }}
    >
      {children}
      <PopOver
        open={popover.open && collapsedNow()}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setPopover((current) => ({ ...current, open: false }))
          }
        }}
      >
        <PopOverContent
          id={popoverId}
          ref={contentRef}
          anchor={popover.anchor}
          side="right"
          align="start"
          sideOffset={12}
          collisionAvoidance={{ side: "none", align: "none", fallbackAxisSide: "none" }}
          className={cn("py-1 px-2.5 overflow-y-auto", {
            "min-w-40": popover.items.length > 0,
          })}
          style={{ maxHeight: popover.maxHeight }}
          onMouseEnter={keepOpen}
          onMouseLeave={scheduleClose}
          onFocusCapture={keepOpen}
          onBlurCapture={scheduleClose}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
              setPopover((current) => ({ ...current, open: false }))
              popover.anchor?.querySelector<HTMLElement>("button")?.focus()
            }
          }}
        >
          <h3
            className={cn("text-ms c-heading--caps text-content-secondary", {
              "border-b border-edge-default pb-1 px-0.5 mb-1!": popover.items.length > 0,
              "mb-0!": popover.items.length === 0,
            })}
          >
            {popover.title}
          </h3>
          {popover.items.length > 0 && <SidebarPopoverItems items={popover.items} />}
        </PopOverContent>
      </PopOver>
    </SidebarPopoverContext.Provider>
  )
}

export const SidebarPopoverTarget = ({
  children,
  items = [],
  openOnInteraction = false,
  targetRef,
  title,
}: {
  children: React.ReactNode
  items?: SidebarPopoverItem[]
  openOnInteraction?: boolean
  targetRef?: React.Ref<HTMLDivElement>
  title: string
}) => {
  const context = useContext(SidebarPopoverContext)

  if (!context) return <>{children}</>

  const { scheduleClose, show } = context
  const showPopover = (anchor: HTMLElement, focusFirst = false) => {
    const rect = anchor.getBoundingClientRect()
    show({
      anchor,
      items,
      maxHeight: Number.isFinite(window.innerHeight - rect.top)
        ? Math.max(120, window.innerHeight - rect.top - 16)
        : 120,
      title,
      focusFirst,
    })
  }

  return (
    <div
      ref={targetRef}
      className="w-full"
      onMouseEnter={(event) => showPopover(event.currentTarget)}
      onClick={
        openOnInteraction && items.length > 0 ? (event) => showPopover(event.currentTarget) : undefined
      }
      onFocus={(event) => showPopover(event.currentTarget)}
      onMouseLeave={scheduleClose}
      onBlur={scheduleClose}
      onKeyDown={
        openOnInteraction && items.length > 0
          ? (event) => {
              if (event.key === "ArrowDown" && context.isCollapsed()) {
                event.preventDefault()
                showPopover(event.currentTarget, true)
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  )
}
